import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDialog } from '@/hooks/useDialog';
import { ViewerIconButton } from './ViewerIconButton';

interface VideoLightboxProps {
  src: string;
  onClose: () => void;
}

/**
 * Full-screen video player. It is the ONLY playing copy of the video: whoever opens it is
 * responsible for pausing any preview, and closing it unmounts the <video>, which stops the
 * sound. Clicking the video (or its controls) never closes it; use the button, Esc, or the
 * dark area around it.
 */
const VideoLightbox = ({ src, onClose }: VideoLightboxProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useDialog(dialogRef, { onClose, initialFocusRef: closeButtonRef });

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Video player"
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/90"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <ViewerIconButton
        ref={closeButtonRef}
        label="Close video player"
        path="M6 6l12 12M18 6L6 18"
        onClick={onClose}
        className="absolute right-4 top-4"
      />

      <video
        src={src}
        className="max-h-[88vh] max-w-[95vw] rounded-lg bg-black"
        autoPlay
        controls
        playsInline
        controlsList="nodownload"
        onContextMenu={(event) => event.preventDefault()}
      />
    </div>,
    document.body
  );
};

export default VideoLightbox;
